import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';
import { isMissingColumnError } from '../utils/supabaseErrors';

type ClientOption = { supabase?: SupabaseClient };

export type EventDayRow = {
  id: string | null;
  event_day: string;
  start_time: string | null;
  end_time: string | null;
  is_dark_day: boolean;
  inventory_custody_state: 'held' | 'released';
  day_options: string[];
  beo_no: string | null;
  updated_at: string | null;
};

export const eventDaysQueryKey = (eventId: string) => ['event-days', eventId];

export async function fetchEventDays(
  eventId: string,
  options?: ClientOption,
): Promise<{ data: EventDayRow[]; error: PostgrestError | null }> {
  if (!eventId) return { data: [], error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  const primaryResult = await supabase
    .from('event_days')
    .select('id,event_day,start_time,end_time,is_dark_day,inventory_custody_state,day_options,beo_no,updated_at')
    .eq('event_id', eventId)
    .is('deleted_at', null)
    .order('event_day', { ascending: true });
  let data: unknown = primaryResult.data;
  let error: PostgrestError | null = primaryResult.error;
  if (error && isMissingColumnError(error, 'inventory_custody_state')) {
    const fallbackResult = await supabase
      .from('event_days')
      .select('id,event_day,start_time,end_time,is_dark_day,day_options,beo_no,updated_at')
      .eq('event_id', eventId)
      .is('deleted_at', null)
      .order('event_day', { ascending: true });
    data = fallbackResult.data;
    error = fallbackResult.error;
  }
  return {
    data: ((data as Array<Partial<EventDayRow>>) || []).map((row) => ({
      ...row,
      id: row.id ?? null,
      event_day: row.event_day ?? '',
      start_time: row.start_time ?? null,
      end_time: row.end_time ?? null,
      is_dark_day: row.is_dark_day === true,
      inventory_custody_state:
        row.inventory_custody_state === 'released' ? 'released' : 'held',
      day_options: Array.isArray(row.day_options) ? row.day_options : [],
      beo_no: row.beo_no ?? null,
      updated_at: row.updated_at ?? null,
    })),
    error: error ?? null,
  };
}

export async function insertEventDays(
  eventId: string,
  rows: Array<{
    event_day: string | null;
    start_time: string | null;
    end_time: string | null;
    is_dark_day: boolean;
    day_options: string[];
    beo_no?: string | null;
    position?: number | null;
  }>,
  options?: ClientOption,
): Promise<{ data: EventDayRow[]; error: PostgrestError | null }> {
  if (!eventId || !rows.length) return { data: [], error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  const payload = rows.map((row, idx) => ({
    event_id: eventId,
    event_day: row.event_day,
    start_time: row.start_time,
    end_time: row.end_time,
    is_dark_day: row.is_dark_day,
    day_options: row.day_options ?? [],
    beo_no: row.beo_no ?? null,
    position: row.position ?? idx,
  }));
  const { data, error } = await supabase
    .from('event_days')
    .insert(payload)
    .select('id,event_day,start_time,end_time,is_dark_day,day_options,beo_no,updated_at');
  return {
    data: ((data as Array<Omit<EventDayRow, 'inventory_custody_state'>>) || []).map((row) => ({
      ...row,
      inventory_custody_state: 'held',
    })),
    error: error ?? null,
  };
}

export async function insertEventDay(
  eventId: string,
  row: {
    event_day: string;
    start_time?: string | null;
    end_time?: string | null;
    is_dark_day?: boolean;
    day_options?: string[];
    beo_no?: string | null;
    position?: number | null;
  },
  options?: ClientOption,
): Promise<{ data: EventDayRow | null; error: PostgrestError | null }> {
  if (!eventId || !row?.event_day) return { data: null, error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  const payload = {
    event_id: eventId,
    event_day: row.event_day,
    start_time: row.start_time ?? null,
    end_time: row.end_time ?? null,
    is_dark_day: row.is_dark_day ?? false,
    day_options: Array.isArray(row.day_options) ? row.day_options : [],
    beo_no: row.beo_no ?? null,
    position: row.position ?? null,
  };
  const { data, error } = await supabase
    .from('event_days')
    .insert(payload)
    .select('id,event_day,start_time,end_time,is_dark_day,day_options,beo_no,updated_at')
    .maybeSingle();
  if (error) return { data: null, error };
  const inserted = data as any;
  return {
    data: {
      id: inserted?.id != null ? String(inserted.id) : null,
      event_day: inserted?.event_day ? String(inserted.event_day) : row.event_day,
      start_time: inserted?.start_time ? String(inserted.start_time) : null,
      end_time: inserted?.end_time ? String(inserted.end_time) : null,
      is_dark_day: !!inserted?.is_dark_day,
      inventory_custody_state: 'held',
      day_options: Array.isArray(inserted?.day_options)
        ? inserted.day_options.filter((value: unknown): value is string => typeof value === 'string')
        : [],
      beo_no: inserted?.beo_no ? String(inserted.beo_no) : null,
      updated_at: inserted?.updated_at ? String(inserted.updated_at) : null,
    },
    error: null,
  };
}

export async function updateEventDay(
  eventId: string,
  day: EventDayRow,
  patch: Partial<EventDayRow>,
  options?: ClientOption,
): Promise<{ updatedAt: string | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const payload: any = {};
  if ('start_time' in patch) payload.start_time = patch.start_time ?? null;
  if ('end_time' in patch) payload.end_time = patch.end_time ?? null;
  if ('is_dark_day' in patch) payload.is_dark_day = patch.is_dark_day ?? undefined;
  if ('day_options' in patch && Array.isArray(patch.day_options)) payload.day_options = patch.day_options;
  if ('beo_no' in patch) payload.beo_no = patch.beo_no ?? null;
  let query = supabase.from('event_days').update(payload);
  const ids = Array.from(new Set((day as any).rowIds?.filter(Boolean) || [])).filter(Boolean);
  if (ids.length === 1) query = query.eq('id', ids[0]);
  else if (ids.length > 1) query = query.in('id', ids);
  else if (day.id) query = query.eq('id', day.id);
  else query = query.eq('event_id', eventId).eq('event_day', day.event_day);
  if (day.updated_at) query = query.eq('updated_at', day.updated_at);
  const result = await query.select('updated_at').maybeSingle();
  if (result.error) return { updatedAt: null, error: result.error };
  const nextUpdatedAt = result.data?.updated_at ? String(result.data.updated_at) : null;
  return { updatedAt: nextUpdatedAt, error: null };
}

export async function softDeleteEventDay(
  eventId: string,
  eventDay: string,
  options?: ClientOption,
): Promise<PostgrestError | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase
    .from('event_days')
    .update({ deleted_at: new Date().toISOString() })
    .eq('event_id', eventId)
    .eq('event_day', eventDay);
  return error ?? null;
}

export function subscribeToEventDays(params: {
  eventId: string;
  supabase?: SupabaseClient;
  onChange: () => void;
}): () => void {
  const supabase = params.supabase ?? getSupabaseClient();
  const channel = supabase
    .channel(`quote:event_days:${params.eventId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'event_days', filter: `event_id=eq.${params.eventId}` },
      () => {
        try {
          params.onChange();
        } catch {
          /* noop */
        }
      },
    )
    .subscribe();

  return () => {
    try {
      channel.unsubscribe();
    } catch {
      /* noop */
    }
  };
}
